import {
  ErrorCode,
  getDetails,
  runWaterfall,
  type VastChain,
  type VastResponse,
  type MediaFile
} from '@rambler-tech/video-ad-sdk'
import debounce from 'lodash.debounce'
import {encodeURIComponentRFC3986} from '@rambler-id/url'
import {
  template,
  type Size,
  Utils,
  PlayerError,
  type FormattedError,
  type Strings,
  type TemplateStringifier
} from '@clappr/core'
import {
  AdvertisementProvider,
  AdvertisementType,
  type AdvertisementSource,
  type AdvertisementVastSource
} from 'common/api/records'
import {isNumber} from 'utils/checkType'
import {parseDuration, parseSkipTime, timeStampToString} from 'utils/date'
import {leftpad} from 'utils/string'
import {captureException} from 'utils/sentry'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import type {Docking} from 'common/plugins/docking'
import type {Notifier} from 'common/plugins/notifier'
import type {MediaControl} from 'common/plugins/media-control'
import type {Advertisement} from 'common/plugins/advertisement'
import {MOBILE_MAX_WIDTH, UNKNOWN_ID} from 'common/constants'
import {AdPlayback} from '../ad-playback'
import {type AdSystem, adSystems} from './ad-systems'
import type {Controls, CustomTracking, VastExtensions} from './extensions'
import * as skins from './skins'

interface Rect {
  width: number
  height: number
  aspectRatio: number
}

type GetClassNames = (params: {height: number}) => [string, boolean][]

export class VastPlayback extends AdPlayback<AdvertisementVastSource> {
  // NOTE: timeout increased to 30s to allow fetching all inlines from wrapper
  public static VAST_LOAD_TIMEOUT = 30000
  public static PREVIEW_DURATION = 3e3
  public static VOLUME = 10
  public static PROGRESS_INTERVAL = 500

  private static loaded = false
  private static template = ''
  private static classNames: Record<string, string> = {}
  private static getContainerClassNames?: GetClassNames

  public static canPlay(source: AdvertisementSource): boolean {
    return (
      source.provider === AdvertisementProvider.VAST &&
      !!source.url &&
      !!adSystems.find(
        // eslint-disable-next-line security/detect-non-literal-regexp
        (system) => system.name && source.url?.match(new RegExp(system.name))
      )
    )
  }

  public static async preload(options: Options): Promise<void> {
    VastPlayback.loaded ??= true

    if (VastPlayback.template) {
      return
    }

    const skinName = options.mediaControl?.skinName
    const loadSkin = (skinName && (skins as any)[skinName]) || skins.rambler

    if (typeof loadSkin !== 'function') {
      return
    }

    const {
      default: template,
      classNames,
      getContainerClassNames
    } = await loadSkin()

    if (template) {
      VastPlayback.template = template
    }

    if (classNames) {
      VastPlayback.classNames = classNames
    }

    VastPlayback.getContainerClassNames = getContainerClassNames
  }

  private static REFETCH_ERRORS = [
    ErrorCode.VAST_LINEAR_ASSET_MISMATCH,
    ErrorCode.VAST_PROBLEM_DISPLAYING_MEDIA_FILE,
    ErrorCode.UNKNOWN_ERROR,
    ErrorCode.VPAID_ERROR
  ]

  private static NOT_STARTED_ERRORS = [
    undefined,
    ErrorCode.VAST_UNEXPECTED_AD_TYPE,
    ErrorCode.VAST_UNEXPECTED_LINEARITY,
    ErrorCode.VAST_UNEXPECTED_DURATION_ERROR,
    ErrorCode.VAST_UNEXPECTED_MEDIA_FILE,
    ErrorCode.VAST_WRAPPER_ERROR,
    ErrorCode.VIDEO_PLAY_ERROR,
    ErrorCode.VAST_MEDIA_FILE_NOT_FOUND,
    ErrorCode.VAST_MEDIA_LOAD_TIMEOUT,
    ErrorCode.VAST_LINEAR_ASSET_MISMATCH,
    ErrorCode.VAST_PROBLEM_DISPLAYING_MEDIA_FILE,
    ErrorCode.VAST_NONLINEAR_PLAYING_FAILED,
    ErrorCode.VAST_NONLINEAR_DIMENSIONS_ERROR,
    ErrorCode.VAST_NONLINEAR_LOADING_FAILED,
    ErrorCode.VAST_NONLINEAR_ASSET_MISMATCH,
    ErrorCode.UNKNOWN_ERROR,
    ErrorCode.VPAID_ERROR
  ]

  private currentTime = 0
  private currentSize?: Size
  private embedSize?: Size
  private started = false
  private destroyed = false
  private adSize?: Size
  private isResizedAfter2s = false
  private adSystem: AdSystem = {}
  private adProgressInterval?: number
  private muted?: boolean
  private volume?: number
  private errors: FormattedError[] = []

  private extensions: VastExtensions = {
    startTime: 0,
    skipTime: -1,
    skipTime2: -1,
    controls: {},
    isClickable: true,
    WOW: false,
    Allowblock: false,
    CustomTracking: {}
  }

  private $el?: ZeptoCollection
  private $panel!: ZeptoCollection
  private $panelText!: ZeptoCollection
  private $blockCount!: ZeptoCollection
  private $oldSkipButton!: ZeptoCollection
  private $acceptButton!: ZeptoCollection
  private $oldAcceptButton!: ZeptoCollection
  private $volumeButton!: ZeptoCollection
  private $seekBar!: ZeptoCollection
  private $preview!: ZeptoCollection
  private $showPreview!: ZeptoCollection
  private $hidePreview!: ZeptoCollection
  private $skip!: ZeptoCollection
  private $skipTime!: ZeptoCollection
  private previewTimeout?: number

  private get template(): TemplateStringifier {
    return template(VastPlayback.template)
  }

  private get classNames(): Record<string, string> {
    return VastPlayback.classNames
  }

  private get getContainerClassNames(): undefined | GetClassNames {
    return VastPlayback.getContainerClassNames
  }

  protected ad?: any
  protected vastChain?: VastChain
  protected details?: any

  public get name(): string {
    return 'vastPlayback'
  }

  public get reloadFailedAdTag(): boolean | undefined {
    return this.adSystem.reloadFailedAdTag
  }

  public get showRemainingTimeToEnd(): boolean {
    return Boolean(this.adSystem.showRemainingTimeToEnd)
  }

  public get allowBlock(): boolean | undefined {
    return (
      this.adSystem.allowBlock ??
      (this.extensions.Allowblock === false && this.extensions.Allowblock)
    )
  }

  public get showBlockNumber(): boolean | undefined {
    return this.adSystem.showBlockNumber
  }

  public get maxBlockLength(): number | undefined {
    return this.adSystem.maxBlockLength
  }

  public get getNextAdBlockSources(): AdSystem['getNextAdBlockSources'] {
    return this.adSystem.getNextAdBlockSources
  }

  // eslint-disable-next-line sonar/function-return-type
  public get controls(): Controls {
    return this.extensions.controls
  }

  public get isWOW(): boolean {
    return this.extensions.WOW
  }

  public get isClickable(): boolean {
    return this.extensions.isClickable
  }

  public get buttonText(): string {
    return this.extensions.buttonText ?? ''
  }

  public get hasAcceptButton(): boolean {
    return (
      this.isClickable &&
      this.controls &&
      !['0', '-1'].includes(this.controls.adlabel)
    )
  }

  public get hasVolumeButton(): boolean {
    return this.controls && !['0', '-1'].includes(this.controls.soundbtn)
  }

  public get skipTime(): number {
    return this.extensions.skipTime
  }

  public get skipTime2(): number {
    return this.extensions.skipTime2
  }

  private get customTracking(): CustomTracking {
    return this.extensions.CustomTracking
  }

  private get hasMediaFiles(): boolean {
    return Boolean(this.details.mediaFiles?.length)
  }

  private get addClick(): string[] {
    return this.extensions.addClick ?? []
  }

  private get skipAd(): string[] {
    return this.extensions.skipAd ?? []
  }

  private get i18n(): Strings<Options> {
    return this.core.i18n
  }

  private get mediaControl(): MediaControl<Options> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<Options> | null
  }

  private get notifier(): Notifier<Options> | null {
    return this.core.getPlugin('notifier') as Notifier<Options> | null
  }

  private get advertisement(): Advertisement<Options> | null {
    return this.core.getPlugin('advertisement') as Advertisement<Options> | null
  }

  private get docking(): Docking<Options> | null {
    return this.core.getPlugin('docking') as Docking<Options> | null
  }

  public async load(source: AdvertisementVastSource): Promise<void> {
    this.sources = {...source}

    this.adSystem =
      adSystems.find(
        (system) =>
          // eslint-disable-next-line security/detect-non-literal-regexp
          system.name && this.sources.url.match(new RegExp(system.name))
      ) ?? {}

    this.sources.url = source.url.replace(/\[sessionid\]/g, Utils.uniqueId(''))
    this.sources.url =
      (await this.adSystem.addUrlParams?.(
        this.sources.url,
        this.core.options
      )) ?? this.sources.url
    this.currentSize = {
      width: this.options.videoElement.offsetWidth,
      height: this.options.videoElement.offsetHeight
    }
    this.adSize = this.adSystem.getSize?.(this.currentSize) ?? this.currentSize
    this.embedSize = this.docking?.getEmbedSize() ?? this.currentSize
    this.trigger(Events.ADPLAYBACK_READY, this.sources)
  }

  public bindControlsEvents(): void {
    this.$volumeButton?.on('click', this.toggleMute)
    this.$oldSkipButton?.on('click', this.skip)
    this.$skip?.on('click', this.skip)
    this.$showPreview?.on('click', this.showPreview)
    this.$hidePreview?.on('click', this.hidePreview)
  }

  public unbindControlsEvents(): void {
    this.$volumeButton?.off('click', this.toggleMute)
    this.$oldSkipButton?.off('click', this.skip)
    this.$skip?.off('click', this.skip)
    this.$showPreview?.off('click', this.showPreview)
    this.$hidePreview?.off('click', this.hidePreview)
  }

  private subscribeOnSizeChanges(): void {
    window.addEventListener('resize', this.applyContainerClassNames)
    window.addEventListener('orientationchange', this.applyContainerClassNames)
    this.applyContainerClassNames()
  }

  private unsubscribeFromSizeChanges(): void {
    window.removeEventListener('resize', this.applyContainerClassNames)
    window.removeEventListener(
      'orientationchange',
      this.applyContainerClassNames
    )

    this.getContainerClassNames?.({
      height: this.core.el.offsetHeight
    })?.forEach(([className]) => {
      this.core.$el.toggleClass(className, false)
    })
  }

  private applyContainerClassNames = debounce((): void => {
    this.getContainerClassNames?.({
      height: this.core.el.offsetHeight
    })?.forEach((entry) => {
      this.core.$el.toggleClass(...entry)
    })
  }, 60)

  protected onAdLoaded = (vastChain: VastChain = []): VastChain => {
    this.errors = []
    this.vastChain = vastChain
    this.details = getDetails(vastChain)
    this.parseExtensions()
    this.parseSkipTimeOffset()
    this.trigger(Events.ADPLAYBACK_LOADED, {
      ...this.sources,
      requestTags: vastChain.map((vast) => vast?.response?.url)
    })
    this.trackURLs(this.customTracking.onVastLoad)

    if (!this.hasMediaFiles) {
      this.trackURLs(this.customTracking.onVastEmpty)
    }

    this.subscribeOnSizeChanges()

    return vastChain
  }

  protected onAdReady = (adUnit: any): void => {
    if (this.destroyed) {
      // NOTE: destroy adUnit if it is loaded after destroy playback
      if (!adUnit.isFinished()) {
        adUnit.cancel()
      }

      return
    }

    const {videoElement, muted} = this.options

    if (muted != null) {
      this.muted = videoElement.muted
      videoElement.muted = muted
    }

    if (this.muted) {
      this.volume = videoElement.volume
      videoElement.volume = 0
    }

    this.ad = adUnit

    if (this.adSize && this.adSize.width !== this.currentSize?.width) {
      this.setAdElementsSize(this.adSize)
    }

    this.ad.on('start', this.onAdStarted)
    this.ad.on('impression', this.onAdImpression)
    this.ad.on('volumeChanged', this.onAdVolumeChange)
    this.ad.on('pause', this.onAdPaused)
    this.ad.on('resume', this.onAdResumed)
    this.ad.on('closeLinear', this.onAdUserClose)
    this.ad.on('clickThrough', this.onAdClick)
    this.ad.on('skip', this.onAdSkipped)
    this.ad.on('complete', this.onAdComplete)
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected onAdStarted = (): void => {
    if (this.adSize) {
      this.ad.resize(this.adSize.width, this.adSize.height, 'normal')
    }

    this.started = true
    this.startAdProgressInterval()
    this.trigger(Events.ADPLAYBACK_STARTED, this.sources)

    const {
      classNames,
      $el,
      advertisement,
      controls,
      isWOW,
      buttonText,
      hasVolumeButton,
      hasAcceptButton,
      showRemainingTimeToEnd,
      skipTime2,
      i18n
    } = this

    if (controls || isWOW) {
      this.$panel.show()
    }

    if (!controls || !advertisement || !$el) {
      return
    }

    this.bindControlsEvents()
    this.changeVolumeUI(this.getVolume())
    this.$panelText.show()
    this.$seekBar.show()
    this.$volumeButton.toggle(hasVolumeButton)
    this.$oldAcceptButton.toggle(hasAcceptButton)

    const duration = this.getDuration()
    const isSkippable = skipTime2 >= 0 && skipTime2 < Math.floor(duration)
    let remainingTimeToSkip

    if (isSkippable) {
      remainingTimeToSkip = skipTime2
    } else if (showRemainingTimeToEnd) {
      remainingTimeToSkip = duration
    }

    if (remainingTimeToSkip != null) {
      this.$skipTime.find('span').html(String(Math.ceil(remainingTimeToSkip)))
      this.showPreview()
    }

    if (buttonText) {
      this.$acceptButton.find('span').html(buttonText)
      this.$oldAcceptButton.find('span').html(buttonText)
    }

    const {allowBlock, blockCompleteCount, maxBlockLength, showBlockNumber} =
      advertisement

    const resultShowBlockNumber =
      allowBlock && showBlockNumber && blockCompleteCount < maxBlockLength

    const currentBlock = blockCompleteCount + 1
    const totalBlocksCount = isFinite(maxBlockLength) ? maxBlockLength : 1

    this.$blockCount.text(
      [
        i18n.t('advertisement_block'),
        currentBlock,
        i18n.t('advertisement_block_of'),
        totalBlocksCount
      ].join(' ')
    )

    $el
      .toggleClass(classNames.withAcceptButton, hasAcceptButton)
      .toggleClass(classNames.withSkip, remainingTimeToSkip != null)
      .toggleClass(classNames.isSkippable, isSkippable)
      .toggleClass(classNames.skipActive, skipTime2 === 0)
      .toggleClass(classNames.withRemainTime, showRemainingTimeToEnd)
      .toggleClass(classNames.withBlockCount, resultShowBlockNumber)
      .toggleClass(classNames.withLabel, !resultShowBlockNumber)
      .toggleClass(classNames.hasMoreBlocks, currentBlock < totalBlocksCount)
      .toggleClass(classNames.timerDisabled, false)

    let panelText: string

    if (resultShowBlockNumber)
      panelText = `${i18n.t(
        'advertisement_block_number'
      )} ${blockCompleteCount}`
    else
      panelText = this.$panelText.hasClass('eagleplayer-advertisement-old')
        ? i18n.t('advertisement')
        : ''

    this.$panelText.html(panelText)
  }

  protected toggleMute = (): void => {
    const volume = this.mediaControl?.muted ? VastPlayback.VOLUME : 0

    this.setVolume(volume)
    this.changeVolumeUI(volume)
    this.mediaControl?.toggleMute()
    this.notifier?.showMessage(
      this.i18n.t(volume ? 'unmuted' : 'muted'),
      this.classNames.volumeMessage
    )
  }

  private changeVolumeUI(volume: number): void {
    const {hasSound} = this.classNames

    if (hasSound) {
      this.$volumeButton.toggleClass(hasSound, volume > 0)
    } else {
      this.$volumeButton.find('.epvi-none').toggle(volume === 0)
      this.$volumeButton.find('.epvi-1').toggle(volume > 0)
      this.$volumeButton.find('.epvi-2').toggle(volume > VastPlayback.VOLUME)
      this.$volumeButton
        .find('.epvi-3')
        .toggle(volume > VastPlayback.VOLUME * 2)
    }
  }

  protected onAdImpression = (): void => {
    this.trigger(Events.ADPLAYBACK_IMPRESSION, this.sources)
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected onAdProgress = (): void => {
    // some vpaid creatives has zero duration and has negative current time
    const creativeDuration = this.ad.duration() ?? 0
    const duration = this.getDuration()
    let currentTime = this.ad.currentTime()

    if (currentTime == null || Number.isNaN(currentTime)) {
      const value = this.ad.videoAdContainer?.videoElement?.currentTime

      if (isNumber(value)) {
        currentTime = value
      }
    }

    this.$el?.toggleClass(
      this.classNames.timerDisabled,
      currentTime == null || Number.isNaN(currentTime)
    )

    if (
      currentTime == null ||
      Number.isNaN(currentTime) ||
      currentTime === this.currentTime
    ) {
      return
    }

    if (currentTime < 0 && creativeDuration === 0 && duration > 0) {
      currentTime += duration

      if (currentTime < 0) {
        currentTime = 0
      }
    }

    this.currentTime = currentTime
    this.trigger(Events.ADPLAYBACK_PROGRESS, {
      ...this.sources,
      current: currentTime,
      total: duration
    })

    if (
      currentTime > 2 &&
      this.adSystem.getSize &&
      this.adSize &&
      this.currentSize &&
      !this.isResizedAfter2s
    ) {
      // set real size for ssp/admail that starts with fake size
      if (this.adSize.width !== this.currentSize.width) {
        this.restoreAdElementsSize()
        this.resize(this.currentSize)
      }

      this.isResizedAfter2s = true
    }

    const {advertisement, controls, isWOW, showRemainingTimeToEnd, skipTime2} =
      this

    if (duration <= 0 || !controls || !advertisement) {
      return
    }

    const {blockCompleteCount, maxBlockLength} = advertisement
    const current = Math.min(currentTime, duration)
    const skipTime = skipTime2 >= 0 && skipTime2 <= duration ? skipTime2 : null
    const remainingTimeToSkip =
      skipTime != null && skipTime > current
        ? Math.ceil(skipTime - current)
        : null
    const remainingTimeToEnd = Math.ceil(duration - current)

    this.$seekBar.css('width', `${(current / duration) * 100}%`)

    let skipTimerTime: undefined | number

    if (blockCompleteCount < maxBlockLength) {
      const xsSize = this.core.activeContainer.el.offsetWidth < MOBILE_MAX_WIDTH

      if (remainingTimeToEnd > 0) {
        if (skipTime == null && !xsSize) {
          skipTimerTime = remainingTimeToEnd
        } else if (showRemainingTimeToEnd) {
          const resultTime = xsSize
            ? remainingTimeToEnd
            : timeStampToString(remainingTimeToEnd * 1000)

          this.$panelText.html(`${resultTime} сек.`)
        }
      }
    }

    if (remainingTimeToSkip != null) {
      skipTimerTime = remainingTimeToSkip
    } else if (isWOW) {
      this.$panel.hide()
    } else if (skipTime != null) {
      this.$oldSkipButton
        .removeClass('eagleplayer-advertisement-skip-button-disabled')
        .show()
    }

    if (skipTimerTime != null) {
      this.$oldSkipButton
        .addClass('eagleplayer-advertisement-skip-button-disabled')
        .show()
        .find('.eagleplayer-advertisement-skip-timer')
        .html(String(skipTimerTime))
    }

    if (skipTime != null || showRemainingTimeToEnd) {
      const remainingTimeValue =
        skipTime != null ? remainingTimeToSkip ?? 0 : remainingTimeToEnd

      this.$skipTime.find('span').html(String(remainingTimeValue))
    }

    this.$el?.toggleClass(
      this.classNames.skipActive,
      skipTime != null && skipTime < current
    )
  }

  protected onAdPaused = (): void => {
    clearInterval(this.adProgressInterval)
    this.trigger(Events.ADPLAYBACK_PAUSED, this.sources)
  }

  protected onAdResumed = (): void => {
    this.startAdProgressInterval()
    this.trigger(Events.ADPLAYBACK_RESUMED, this.sources)
  }

  protected onAdUserClose = (): void => {
    this.trigger(Events.ADPLAYBACK_USER_CLOSE, this.sources)
  }

  protected onAdVolumeChange = (): void => {
    this.trigger(Events.ADPLAYBACK_VOLUME_CHANGE, this.sources)
  }

  protected onAdClick = (): void => {
    this.trigger(Events.ADPLAYBACK_CLICK, this.sources)
    this.trackURLs(this.addClick)
  }

  protected onAdSkipped = (): void => {
    this.trigger(Events.ADPLAYBACK_SKIPPED, this.sources)
  }

  protected onAdComplete = (): void => {
    this.trigger(Events.ADPLAYBACK_COMPLETE, this.sources)
  }

  protected onAdError = (error: any, errorInfo: any): void => {
    const requestTags =
      errorInfo.vastChain?.map((vast: VastResponse) => vast?.response?.url) ??
      []

    const canRefetch = VastPlayback.REFETCH_ERRORS.includes(error.code)

    const formattedError = this.createError({
      code: `${error.code ?? UNKNOWN_ID}`,
      description: error.message,
      level: PlayerError.Levels.WARN,
      raw: {...this.sources, requestTags, canRefetch, code: error.code}
    })

    if (error.message !== 'No next ad to request') {
      this.errors.unshift(formattedError)
    }

    this.trigger(Events.ADPLAYBACK_WARNING, formattedError)
  }

  protected onAdFinish = (): void => {
    if (this.destroyed) {
      return
    }

    this.restoreDefaultVideoElement()

    const [vastError] = this.errors

    const adIsLoadedButNotStarted =
      !this.started &&
      VastPlayback.NOT_STARTED_ERRORS.includes(vastError?.raw?.code)

    if (this.vastChain && adIsLoadedButNotStarted) {
      // NOTE: capture error if vast config is loaded but isn't started (drop-off)
      const error = new Error('the vast ad was loaded but did not start')
      const requestTags = this.vastChain.map((vast) => vast?.response?.url)
      const XML = this.vastChain.slice(0, 1).map((vast) => vast?.XML)
      const extra: Record<string, any> = {requestTags, XML}

      if (vastError) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {raw, ...error} = vastError

        extra.vastError = {
          ...error,
          raw: {
            index: raw?.index,
            mute: raw?.mute,
            type: raw?.type
          }
        }
      }

      captureException(error, {
        ...extra,
        errorCode: extra.vastError ? String(extra.vastError.code) : UNKNOWN_ID
      })
    }

    if (!vastError) {
      this.trigger(Events.ADPLAYBACK_CONTENT_RESUME_REQUESTED, this.sources)

      return
    }

    this.trigger(Events.ADPLAYBACK_ERROR, vastError)
  }

  private startAdProgressInterval(): void {
    if (this.isLinear()) {
      this.adProgressInterval = window.setInterval(
        this.onAdProgress,
        VastPlayback.PROGRESS_INTERVAL
      )
    }
  }

  private setAdElementsSize(size: Size): void {
    this.options.videoElement.style.width = `${size.width}px`
    this.options.videoElement.style.height = `${size.height}px`

    if (this.ad?.videoAdContainer) {
      this.ad.videoAdContainer.element.style.width = `${size.width}px`
      this.ad.videoAdContainer.element.style.height = `${size.height}px`
    }
  }

  private restoreAdElementsSize(): void {
    this.restoreDefaultVideoElement()

    if (this.ad?.videoAdContainer) {
      this.ad.videoAdContainer.element.style.width = '100%'
      this.ad.videoAdContainer.element.style.height = '100%'
    }
  }

  private restoreDefaultVideoElement(): void {
    if (this.adSize?.width !== this.currentSize?.width) {
      this.options.videoElement.style.width = '100%'
      this.options.videoElement.style.height = '100%'
    }

    if (this.muted != null) {
      this.options.videoElement.muted = this.muted
    }

    if (this.volume != null) {
      this.options.videoElement.volume = this.volume
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private parseExtensions(): void {
    // NOTE: copy vast chain to prevent mutate original array by reverse
    const chain = [...(this.vastChain ?? [])]

    // NOTE: wrapper extensions override the same nested inline/wrapper extensions
    const nodes = chain.reduce(
      (acc: any[], vast) => [
        ...acc,
        ...(vast?.ad?.elements?.[0]?.elements?.filter(
          (element: any) => element.name === 'extensions'
        )[0]?.elements ?? [])
      ],
      []
    )

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]
      const type = node.attributes?.type as string | undefined
      const children = node.elements
      const textContent = children?.[0]?.text?.trim() as string | undefined

      if (
        (type === 'startTime' || type === 'skipTime' || type === 'skipTime2') &&
        textContent
      ) {
        this.extensions[type] = parseSkipTime(textContent)
      }

      if (type === 'controls') {
        this.extensions[type] = this.parseControlNodes(children)
      }

      if (type === 'isClickable') {
        this.extensions[type] = textContent !== '0'
      }

      if (type === 'WOW' || type === 'Allowblock') {
        this.extensions[type] = textContent === '1'
      }

      if (type === 'CustomTracking') {
        this.extensions[type] = this.parseCustomTrackingNodes(children)
      }

      if ((type === 'skipAd' || type === 'addClick') && textContent) {
        this.extensions[type] = [textContent]
      }

      if ((type === 'linkTxt' || type === 'button_text') && textContent) {
        this.extensions.buttonText = decodeURIComponent(textContent)
      }
    }
  }

  // eslint-disable-next-line sonar/function-return-type
  private parseControlNodes(children: any[]): Controls {
    const textContent = children?.[0]?.text?.trim() as string | undefined

    if (textContent === '0') {
      return false
    }

    const result: Record<string, string> = {}

    for (let i = 0; i < children.length; i++) {
      const node = children[i]
      const id = node.attributes?.id
      const value = node.attributes?.layout?.trim()

      if (id && value) {
        result[id] = value
      }
    }

    return result
  }

  private parseCustomTrackingNodes(children: any[]): CustomTracking {
    const result: CustomTracking = {}

    for (let i = 0; i < children.length; i++) {
      const node = children[i]
      let eventName = node.attributes?.event
      const textContent = node.elements?.[0]?.text?.trim()

      if (node.name !== 'Tracking' || !eventName || !textContent) {
        continue
      }

      if (eventName === 'progress') {
        const offset = node.attributes?.offset

        if (!offset) {
          continue
        }

        eventName =
          offset.charAt(offset.length - 1) === '%'
            ? `progress-${offset}`
            : `progress-${Math.round(parseDuration(offset))}`
      }

      if (!Array.isArray(result[eventName])) {
        result[eventName] = []
      }

      result[eventName]?.push(textContent)
    }

    return result
  }

  private parseSkipTimeOffset(): void {
    const {durationInMs, skipOffsetInMs} = this.details

    if (this.extensions.skipTime >= 0 || this.extensions.skipTime2 >= 0) {
      this.extensions =
        this.adSystem.getSkipTime?.(this.extensions, durationInMs / 1000) ??
        this.extensions
    }

    if (
      this.extensions.skipTime2 < 0 &&
      skipOffsetInMs != null &&
      skipOffsetInMs >= 0
    ) {
      this.extensions.skipTime2 = skipOffsetInMs / 1000
    }
  }

  private trackURLs(urlTemplates?: string[]): void {
    if (!urlTemplates || urlTemplates.length === 0) {
      return
    }

    const variables: Record<string, string> = {}

    if (this.isLinear()) {
      const {mediaFiles, durationInMs} = this.details

      if (mediaFiles[0]?.src) {
        variables.ASSETURI = encodeURIComponentRFC3986(mediaFiles[0].src)
      }

      if (durationInMs) {
        variables.CONTENTPLAYHEAD = encodeURIComponentRFC3986(
          timeStampToString(durationInMs, {hours: true, ms: true})
        )
      }
    }

    // random/time based macros
    variables.CACHEBUSTING = leftpad(
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      Math.round(Math.random() * 1.0e8).toString(),
      '0',
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      8
    )

    variables.TIMESTAMP = encodeURIComponentRFC3986(new Date().toISOString())

    // RANDOM/random is not defined in VAST 3/4 as a valid macro tho it's used by some adServer
    variables.RANDOM = variables.random = variables.CACHEBUSTING

    urlTemplates.forEach((url) => {
      if (typeof url !== 'string') {
        return
      }

      let imageUrl = url

      for (const key in variables) {
        if (variables.hasOwnProperty(key)) {
          const value = variables[key]

          imageUrl = url.replace(`[${key}]`, value).replace(`%%${key}%%`, value)
        }
      }

      const image = new Image()

      image.src = imageUrl
    }, {})
  }

  private createInvisibleElement(): HTMLDivElement {
    const element = document.createElement('div')

    element.style.position = 'absolute'
    element.style.width = '0px'
    element.style.height = '0px'
    element.style.display = 'none'

    return element
  }

  private getRectParams = ({width = 0, height = 0}: Partial<Rect>): Rect => {
    // NOTE: leaving 1 digit after the decimal point to handle
    //       approximately equal ratios, because aspect ratio
    //       of 480p (854x480) don't strict equal to 360p (640x360)
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const aspectRatio = height ? Math.floor((width / height) * 10) / 10 : 0

    return {
      aspectRatio,
      height,
      width
    }
  }

  private getMediaFile = (mediaFiles: MediaFile[]): any => {
    if (!this.embedSize) {
      return
    }

    const embedRect = this.getRectParams({
      width: Number(this.embedSize.width),
      height: Number(this.embedSize.height)
    })

    const compareTo = (
      mediaFileA: MediaFile,
      mediaFileB: MediaFile
    ): number => {
      const mediaFileARect = this.getRectParams({
        width: Number(mediaFileA.width),
        height: Number(mediaFileA.height)
      })

      const mediaFileBRect = this.getRectParams({
        width: Number(mediaFileB.width),
        height: Number(mediaFileB.height)
      })

      const widthDeltaA = Math.abs(embedRect.width - mediaFileARect.width)
      const widthDeltaB = Math.abs(embedRect.width - mediaFileBRect.width)

      const aspectRatioDeltaA = Math.abs(
        embedRect.aspectRatio - mediaFileARect.aspectRatio
      )

      const aspectRatioDeltaB = Math.abs(
        embedRect.aspectRatio - mediaFileBRect.aspectRatio
      )

      return aspectRatioDeltaA - aspectRatioDeltaB || widthDeltaA - widthDeltaB
    }

    const sortedMediaFiles = mediaFiles.slice(0).sort(compareTo)

    return sortedMediaFiles[0]
  }

  public start(): void {
    const {advertisement} = this
    if (this.destroyed || !advertisement) return

    this.render()

    const {containerElement, videoElement, clickTrackingElement} = this.options
    const clickElement = advertisement.channel?.clickElement
    const clickControl =
      (this.hasAcceptButton &&
        clickElement === 'button' &&
        (this.$acceptButton.get(0) ?? this.$oldAcceptButton.get(0))) ||
      clickTrackingElement

    runWaterfall(this.sources.url, containerElement, {
      videoElement,
      responsive: true,
      pauseOnAdClick: false,
      timeout: VastPlayback.VAST_LOAD_TIMEOUT,
      onAdReady: this.onAdReady,
      onError: this.onAdError,
      onRunFinish: this.onAdFinish,
      hooks: {
        createSkipControl: this.createInvisibleElement,
        createClickControl: clickControl && (() => clickControl),
        getMediaFile: this.getMediaFile,
        // NOTE: use transform hook to trigger loaded state
        // - waterfall don't trigger loaded event
        // - transform is applied when all wrappers/inline are loaded
        transformVastResponse: this.onAdLoaded
      }
    })
  }

  public pause(): void {
    if (this.ad?.isStarted()) {
      this.ad?.pause()
    }
  }

  public resume(): void {
    if (this.ad?.isStarted()) {
      this.ad?.resume()
    }
  }

  public skip = (): void => {
    const {skipOffsetInMs} = this.details

    this.trackURLs(this.skipAd)
    this.trackURLs(this.customTracking.onSkipAd)

    if (skipOffsetInMs != null && skipOffsetInMs >= 0) {
      this.ad?.skip()

      return
    }

    this.ad?.cancel()
  }

  public setVolume(volume: number): void {
    this.ad?.setVolume(volume / 100)
  }

  public getVolume(): number {
    const volume = this.ad?.getVolume() ?? 0

    return volume * 100
  }

  public resize(size: Size): void {
    if (!this.ad?.isFinished()) {
      const nextAdSize = this.adSystem.getSize?.(size, this.currentTime) ?? size

      // set real size for ssp/admail that starts with fake size
      if (
        this.currentTime <= 2 &&
        this.adSystem.getSize &&
        this.adSize &&
        this.currentSize
      ) {
        if (nextAdSize.width !== size.width) {
          this.setAdElementsSize(nextAdSize)
        } else if (this.adSize.width !== this.currentSize.width) {
          this.restoreAdElementsSize()
        }
      }

      this.currentSize = size
      this.adSize = nextAdSize

      if (this.ad?.isStarted()) {
        this.ad?.resize(this.adSize.width, this.adSize.height, 'normal')
      }
    }
  }

  public getDuration(): number {
    const duration = this.isLinear()
      ? this.ad?.duration() || this.details.durationInMs / 1000
      : this.ad?.duration()

    return duration ?? 0
  }

  public getCurrentTime(): number {
    return this.currentTime
  }

  public isLinear(): boolean {
    return this.details?.durationInMs != null
  }

  public getAdSystem(): string {
    return this.details?.adSystem ?? UNKNOWN_ID
  }

  public getAdType(): AdvertisementType {
    if (!this.details) {
      return AdvertisementType.UNKNOWN
    }

    return this.details?.vpaid
      ? AdvertisementType.VPAID
      : AdvertisementType.VAST
  }

  public destroy(): void {
    clearInterval(this.adProgressInterval)
    this.destroyed = true

    if (!this.ad?.isFinished()) {
      this.ad?.cancel()
    }

    this.hideControls()
    this.unbindControlsEvents()
    this.unsubscribeFromSizeChanges()
    this.$el?.remove()
    delete this.$el
  }

  protected setPreviewTimeout = (): void => {
    this.clearPreviewTimeout()
    this.previewTimeout = window.setTimeout(
      this.hidePreview.bind(this),
      VastPlayback.PREVIEW_DURATION
    )
  }

  protected clearPreviewTimeout = (): void => {
    window.clearTimeout(this.previewTimeout)
  }

  private showPreview = (): void => {
    this.$preview.toggleClass(this.classNames.previewVisible, true)
    this.setPreviewTimeout()
  }

  private hidePreview = (): void => {
    this.clearPreviewTimeout?.()
    this.$preview.toggleClass(this.classNames.previewVisible, false)
  }

  private hideControls(): void {
    const {advertisement, $el} = this
    if (!advertisement || !$el) return

    this.$panel.hide()
    this.$panelText.hide()
    this.hidePreview()
    $el
      .toggleClass(this.classNames.withAcceptButton, false)
      .toggleClass(this.classNames.withSkip, false)
      .toggleClass(this.classNames.withRemainTime, false)
      .toggleClass(this.classNames.withBlockCount, false)
      .toggleClass(this.classNames.withLabel, false)
    this.$oldSkipButton.hide()
    this.$oldAcceptButton.hide()
    this.$volumeButton.hide()
    this.$seekBar.hide()
  }

  public render(): this {
    const {advertisement} = this

    if (!this.core.$el.parents().length || this.$el || !advertisement) {
      return this
    }

    const {playList} = this.core.options

    this.$el = $(
      this.template({
        preview: playList?.snapshot || 'none',
        title: playList?.title ?? '',
        duration: playList ? timeStampToString(playList.duration) : '',
        i18n: Object.fromEntries(
          [
            'advertisement',
            'advertisement_link',
            'advertisement_after',
            'advertisement_skip',
            'advertisement_skip_timer',
            'advertisement_watch',
            'advertisement_watch_timer',
            'advertisement_block',
            'advertisement_block_of',
            'unmute'
          ].map((key) => [key, this.core.i18n.t(key)])
        )
      })
    )

    advertisement.$el.append(this.$el)

    // TODO(VP-4598): Избавиться от использования классов для поиска элементов
    // Использование дата аттрибутов имеет приоритет
    this.$panel = this.$el.find('.eagleplayer-advertisement-panel')
    this.$panelText = this.$el.find('.eagleplayer-advertisement-panel-text')
    this.$preview = this.$el.find('[data-ad-preview]')
    this.$showPreview = this.$el.find('[data-ad-preview-show]')
    this.$hidePreview = this.$el.find('[data-ad-preview-hide]')
    this.$skip = this.$el.find('[data-ad-skip]')
    this.$skipTime = this.$el.find('[data-ad-skip-time]')
    this.$blockCount = this.$el.find('[data-ad-block-count]')
    this.$oldSkipButton = this.$el.find(
      '.eagleplayer-advertisement-skip-button'
    )
    this.$acceptButton = this.$el.find('[data-ad-accept]')
    this.$oldAcceptButton = this.$el.find(
      '.eagleplayer-advertisement-accept-button'
    )
    this.$volumeButton = this.$el.find(
      '[data-ad-volume], .eagleplayer-advertisement-volume-button'
    )
    this.$seekBar = this.$el.find(
      '[data-ad-seek], .eagleplayer-advertisement-seek-bar'
    )

    this.hideControls()

    return this
  }
}
